import { useEffect, useMemo, useState } from "react";
import { Package, Download, Check, X, Edit3, Search, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "text-amber-300 border-amber-300/40 bg-amber-400/10",
  approved: "text-emerald-300 border-emerald-300/40 bg-emerald-400/10",
  rejected: "text-red-300 border-red-300/40 bg-red-400/10",
};

/** Admin panel — /admin/prints — list of every 3D print quote, with expandable
 *  detail row for approve / adjust / reject actions. */
export default function AdminPrintsQuotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const q = status && status !== "all" ? `?status=${status}` : "";
      const r = await api.get(`/admin/prints/quotes${q}`);
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(apiError(err, "Failed to load quotes"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(filter); }, [filter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((it) =>
      it.id.toLowerCase().includes(q) ||
      it.file_name.toLowerCase().includes(q) ||
      it.contact?.email?.toLowerCase().includes(q) ||
      it.contact?.name?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const decide = async (id, payload) => {
    try {
      await api.post(`/admin/prints/quotes/${id}/decide`, payload);
      toast.success("Decision recorded.");
      setExpanded(null);
      load(filter);
    } catch (err) {
      toast.error(apiError(err, "Decision failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-prints-quotes">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Package className="h-4 w-4 text-fuchsia-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">Portal · 3D Print Quotes</span>
        </div>
        <h2 className="font-display text-2xl tracking-[0.05em] text-white">Quote Review</h2>
        <p className="text-white/55 text-sm mt-1">Every incoming quote lands here. Approve as-is, adjust the price, or reject with a note.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            data-testid={`admin-quotes-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`font-mono text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border transition-colors ${
              filter === s ? "border-fuchsia-300/60 bg-fuchsia-400/10 text-fuchsia-300" : "border-white/10 text-white/50 hover:text-white hover:border-white/30"
            }`}
          >
            {s}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            data-testid="admin-quotes-search"
            placeholder="Search by ID, email, name, filename…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 p-8 text-center text-white/50" data-testid="admin-quotes-empty">
          No quotes {filter !== "all" ? `in ${filter}` : ""} yet.
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-[0.24em] text-white/50">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">File</th>
                <th className="px-4 py-2 text-right">Weight</th>
                <th className="px-4 py-2 text-right">Est.</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-right">Received</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <QuoteRow
                  key={it.id}
                  quote={it}
                  expanded={expanded === it.id}
                  onToggle={() => setExpanded(expanded === it.id ? null : it.id)}
                  onDecide={(payload) => decide(it.id, payload)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QuoteRow({ quote, expanded, onToggle, onDecide }) {
  const statusClass = STATUS_COLORS[quote.status] || "text-white/60 border-white/10 bg-white/5";
  const [finalPrice, setFinalPrice] = useState(quote.final_price_usd ?? quote.quote?.total_usd ?? 0);
  const [note, setNote] = useState(quote.admin_note || "");
  useEffect(() => {
    setFinalPrice(quote.final_price_usd ?? quote.quote?.total_usd ?? 0);
    setNote(quote.admin_note || "");
  }, [quote.id, quote.final_price_usd, quote.quote?.total_usd, quote.admin_note]);

  return (
    <>
      <tr
        onClick={onToggle}
        className={`border-t border-white/5 cursor-pointer transition-colors ${expanded ? "bg-white/5" : "hover:bg-white/[0.03]"}`}
        data-testid={`admin-quote-row-${quote.id}`}
      >
        <td className="px-4 py-3 font-mono text-[11px] text-white/80">
          {quote.id.slice(0, 8).toUpperCase()}
          {quote.needs_manual_review && (
            <div className="mt-1 inline-block font-mono text-[9px] uppercase tracking-[0.22em] text-amber-300 border border-amber-300/40 bg-amber-400/10 rounded-full px-1.5 py-0.5" title={quote.bed_over_reason || "Flagged for manual review"} data-testid="admin-quote-manual-review-badge">
              Review
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="text-white truncate max-w-[220px]" data-testid="admin-quote-customer">{quote.contact?.name}</div>
          <div className="text-[11px] text-white/45 truncate max-w-[220px]">{quote.contact?.email}</div>
        </td>
        <td className="px-4 py-3">
          <div className="text-white/85 truncate max-w-[240px]">{quote.file_name}</div>
          <div className="text-[11px] text-white/45">
            {(quote.file_size_bytes / 1024 / 1024).toFixed(1)} MB
            {quote.file_is_oversize && <span className="text-amber-300 ml-1">· Dropbox</span>}
          </div>
        </td>
        <td className="px-4 py-3 text-right font-mono tabular-nums text-white/80">{quote.quote?.weight_g?.toFixed?.(1)}g</td>
        <td className="px-4 py-3 text-right font-mono tabular-nums text-white">
          ${quote.quote?.total_usd?.toFixed?.(2)}
          {quote.final_price_usd != null && quote.final_price_usd !== quote.quote?.total_usd && (
            <div className="text-[10px] text-emerald-300 mt-0.5">→ ${quote.final_price_usd.toFixed(2)}</div>
          )}
        </td>
        <td className="px-4 py-3 text-center">
          <span className={`font-mono text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full border ${statusClass}`}>
            {quote.status}
          </span>
        </td>
        <td className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
          {new Date(quote.created_at).toLocaleString()}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-black/40 border-t border-white/5">
          <td colSpan={7} className="px-6 py-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Model + Geometry */}
              <div>
                <SectionHead>File</SectionHead>
                <div className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4" data-testid="admin-quote-file-panel">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-fuchsia-400/10 border border-fuchsia-300/30 flex items-center justify-center shrink-0">
                      <Package className="h-5 w-5 text-fuchsia-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white/90 text-sm truncate" title={quote.file_name}>{quote.file_name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45 mt-0.5">
                        {quote.file_ext?.toUpperCase?.().replace(".", "") || "—"} · {(quote.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  {quote.file_ref ? (
                    <a
                      href={`${API}/prints/upload/${quote.file_ref}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-fuchsia-400 hover:bg-fuchsia-300 text-black font-mono text-[11px] uppercase tracking-[0.24em] px-3 py-2.5 transition-colors"
                      data-testid="admin-quote-download"
                    >
                      <Download className="h-4 w-4" /> Download for slicing
                    </a>
                  ) : quote.file_is_oversize ? (
                    <div className="mt-3 rounded-lg border border-amber-300/40 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-100" data-testid="admin-quote-oversize">
                      <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-amber-300 mb-0.5">Oversize · Dropbox handoff</div>
                      <div>File exceeds the upload limit. Reach out to the customer at the email/phone on the right and ask them to send it via Dropbox / WeTransfer.</div>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/60" data-testid="admin-quote-nofile">
                      No uploaded file for this quote. Contact the customer to have them re-submit.
                    </div>
                  )}
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/35 mt-2">Files auto-purge 3 days after submission.</div>
                </div>
                <SectionHead>Geometry</SectionHead>
                <Detail label="Format" value={quote.file_ext.toUpperCase()} />
                <Detail label="Raw volume" value={`${quote.raw_volume_mm3.toFixed(0)} mm³`} />
                <Detail label="Scale" value={`${(quote.scale * 100).toFixed(0)}%`} />
                <Detail label="Scaled" value={`${quote.quote?.scaled_volume_mm3?.toFixed?.(0)} mm³`} />
                <Detail label="Printed (incl. supports)" value={`${quote.quote?.printed_volume_mm3?.toFixed?.(0)} mm³`} />
                <Detail label="Bounding box"
                  value={`${(quote.bbox_mm.x * quote.scale).toFixed(1)} × ${(quote.bbox_mm.y * quote.scale).toFixed(1)} × ${(quote.bbox_mm.z * quote.scale).toFixed(1)} mm`}
                />
                {quote.needs_manual_review && (
                  <div className="mt-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-100" data-testid="admin-quote-review-notice">
                    <div className="font-mono uppercase tracking-[0.2em] text-[10px] text-amber-300">Needs manual review</div>
                    <div className="mt-0.5">{quote.bed_over_reason || "Flagged by customer — please double-check feasibility."}</div>
                  </div>
                )}
                <Detail label="Weight (each)" value={`${quote.quote?.weight_g?.toFixed?.(1)} g`} />
                <Detail label="Quantity" value={quote.quantity} />
                <Detail label="Material" value={quote.quote?.material_label} />
                <Detail label="Print time (each)" value={quote.quote?.print_hours ? `${quote.quote.print_hours.toFixed(2)} hr (${quote.quote.layer_count} layers)` : "—"} />
                <Detail label="Time cost × qty" value={`$${quote.quote?.time_total_usd?.toFixed?.(2) ?? "—"}`} />
                <Detail label="Resin (× qty)" value={`$${quote.quote?.material_total_usd?.toFixed?.(2) ?? "—"}`} />
                <Detail label="Curing & clean (× qty)" value={`$${(quote.quote?.curing_total_usd ?? quote.quote?.setup_fee_usd)?.toFixed?.(2) ?? "—"}`} />
                <Detail label="Auto-quote" value={`$${quote.quote?.total_usd?.toFixed?.(2)}`} />
              </div>

              {/* Contact + Shipping */}
              <div>
                <SectionHead>Contact & Shipping</SectionHead>
                <Detail label="Name" value={quote.contact?.name} />
                <Detail label="Email" value={quote.contact?.email} copy />
                <Detail label="Phone" value={quote.contact?.phone} copy />
                <div className="mt-2 text-sm text-white/80 leading-relaxed">
                  {quote.shipping?.line1}
                  {quote.shipping?.line2 ? <><br />{quote.shipping.line2}</> : null}
                  <br />{quote.shipping?.city}, {quote.shipping?.region} {quote.shipping?.postal_code}
                  <br />{quote.shipping?.country}
                </div>
                {quote.notes && (
                  <>
                    <SectionHead className="mt-4">Notes</SectionHead>
                    <div className="text-sm text-white/70 whitespace-pre-wrap">{quote.notes}</div>
                  </>
                )}
              </div>

              {/* Decision */}
              <div>
                <SectionHead>Decision</SectionHead>
                {quote.status === "pending" ? (
                  <>
                    <div className="mb-3">
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/60 mb-1">Final price (USD)</div>
                      <Input
                        data-testid="admin-quote-final-price"
                        type="number"
                        step="0.01"
                        value={finalPrice}
                        onChange={(e) => setFinalPrice(parseFloat(e.target.value || "0"))}
                        className="bg-white/5 border-white/10 text-white h-10"
                      />
                      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/40 mt-1">
                        Auto-quote: ${quote.quote?.total_usd?.toFixed?.(2)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/60 mb-1">Note to customer (optional)</div>
                      <Textarea
                        data-testid="admin-quote-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Reduced price due to hollow print, added shipping, etc."
                        className="bg-white/5 border-white/10 text-white min-h-[70px]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        data-testid="admin-quote-approve"
                        onClick={() => onDecide({ action: "approve", final_price_usd: finalPrice, admin_note: note })}
                        className="bg-emerald-400 text-black hover:bg-emerald-300 w-full"
                      >
                        <Check className="h-4 w-4 mr-1.5" /> Approve at ${finalPrice.toFixed(2)}
                      </Button>
                      <Button
                        data-testid="admin-quote-adjust"
                        variant="outline"
                        onClick={() => onDecide({ action: "adjust", final_price_usd: finalPrice, admin_note: note })}
                        className="border-fuchsia-300/40 text-fuchsia-300 hover:bg-fuchsia-400/10 w-full"
                      >
                        <Edit3 className="h-4 w-4 mr-1.5" /> Adjust & approve
                      </Button>
                      <Button
                        data-testid="admin-quote-reject"
                        variant="outline"
                        onClick={() => onDecide({ action: "reject", admin_note: note })}
                        className="border-red-300/40 text-red-300 hover:bg-red-400/10 w-full"
                      >
                        <X className="h-4 w-4 mr-1.5" /> Reject
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-white/70">
                    <div>Status: <span className="text-white">{quote.status}</span></div>
                    {quote.final_price_usd != null && <div className="mt-1">Final: <span className="text-white font-mono">${quote.final_price_usd.toFixed(2)}</span></div>}
                    {quote.admin_note && <div className="mt-2 text-white/60">{quote.admin_note}</div>}
                    {quote.decided_at && <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">Decided {new Date(quote.decided_at).toLocaleString()}</div>}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function SectionHead({ children, className = "" }) {
  return <div className={`font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2 ${className}`}>{children}</div>;
}

function Detail({ label, value, copy = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5 text-sm">
      <span className="text-white/45 font-mono text-[10px] uppercase tracking-[0.22em]">{label}</span>
      <span className="text-white/85 font-mono tabular-nums truncate inline-flex items-center gap-2">
        {value}
        {copy && value && (
          <button
            onClick={() => { navigator.clipboard.writeText(String(value)); toast.success("Copied"); }}
            className="text-white/40 hover:text-fuchsia-300"
            title="Copy"
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </span>
    </div>
  );
}
